/**
 * One tick of the roster, in passes. The pass order is the whole determinism
 * contract, so it is written out once here and nowhere else:
 *
 * 1. **Decide** — every AI reads the *pre-step* world and produces inputs.
 * 2. **Fly** — every sim takes its `step`. Guns, jams and the belt are resolved
 *    inside that step by the existing gun model.
 * 2b. **Archie** — every flak battery lays, fires and resolves against the
 *    *pre-step* poses, for the same reason the guns do: a battery aims at the
 *    picture it can see. Ground fire is deliberately its own pass rather than a
 *    shooter in pass 3, because a shell is not a round — it is not swept, it
 *    cannot be dodged once it is up, and it draws from its own `flak` stream
 *    (`flak.ts`).
 * 3. **Shoot** — rounds that left the barrels this tick are spawned from the
 *    *pre-step* pose of the shooter: the picture the pilot was actually looking
 *    at. Then every round in the air, new and old, is swept against the
 *    pre-step poses of everything it could run into (`collide.ts`). A round
 *    that connects is consumed; the rest fly on and are tested again next tick.
 *    Nothing here is a probability — hits are the geometry and only the
 *    geometry.
 * 3a. **Scenery** — the rounds that ran into nothing that flies are swept
 *    against what is standing on the ground (`scenery-pass.ts`, AD-018).
 *    Deliberately after the gunnery sweep and over its *survivors only*: an
 *    aeroplane in front of a barn still takes the round, because `collide.ts`
 *    already consumed it. A port extension, and inert unless the caller injected
 *    a `SceneryQuery`.
 * 3b. **Ram** — everything that moved this tick is asked whether it ended up
 *    inside something else (`ram-pass.ts`, AD-017). Unlike the guns, this runs
 *    against the *stepped* poses: nobody aims a collision, and the original's
 *    `move.c` likewise moves an object before asking. It is its own pass rather
 *    than part of the sweep because a collision is not a projectile — it has no
 *    shooter, no flight time and no roll, and it can set a Zeppelin alight,
 *    which no round does on its own.
 * 3c. **Strike** — the player is asked whether he flew into a building
 *    (`building-strike.ts`, AD-020), on 3b's stepped poses and after it, and it
 *    sets what he hit alight. A declared enhancement — the original's collision
 *    check has no scenery in it — and inert without a `SceneryQuery`, as 3a is.
 * 4. **Bomb** — bombers with a solution let one go, from the same pre-step pose
 *    and for the same reason; a player-flown rack lets one go because the pilot
 *    pulled the toggle, which is an *input* and therefore lockstep-safe by
 *    construction. Then every bomb in the air falls one tick and the ones that
 *    reached the ground go off (`bombs.ts`).
 * 5. **Wound** — hits land on the stepped states, so damage is visible from the
 *    tick it happened.
 * 6. **Burn** — fires eat the airframe.
 * 7. **Judge** — statuses settle.
 *
 * Balloons, Zeppelins and ground targets (`targets.ts`) ride along in passes 3,
 * 4, 6 and 7: they are shot at with the same gunnery solver aeroplanes are, they
 * are bombed, they burn, and they are judged. They take no part in passes 1 and
 * 2 because they have neither a pilot nor an aerodynamic model — a tethered
 * gasbag's whole flight dynamics is "stay put", a Zeppelin's is "keep going",
 * and a railway yard's is nothing at all.
 *
 * **A Zeppelin also *shoots* in pass 3**, which is new and is the one thing on
 * that list a target does rather than has done to it. `MG.DAT` gives it four
 * gun stations with real traverse arcs, and they fire from inside `gun-pass.ts`
 * — not from a pass of their own — so their rounds are the same records
 * everything else's are. It still takes no part in passes 1 and 2: its gunners
 * are not a pilot, they have no aerodynamic model, and they aim by traversing a
 * mount rather than by flying the aeroplane. See `airship-pass.ts`.
 *
 * **A ship shoots in both 2b and 3, and moves before either.** ENHANCED
 * SIMULATION's escort sloop is a hull on the target list and a `FlakBattery` on
 * the flak list at once (`ship-pass.ts`), so pass **0** — `steam`, below —
 * rewrites both from her patrol before anything aims at anything. That is the
 * pre-step rule applied to a thing whose position is a function of the clock
 * rather than of a force balance: everything on this tick sees the ship where
 * she is at the top of it. **A ship that has been sunk is placed by the same
 * pass** — `ship-damage.ts` answers both regimes from one call — and her
 * battery is the one thing that leaves a list rather than being rewritten in
 * it, because `flak.ts` has no way to be told to hold fire. With no ships the
 * pass is one array-length test and returns the arrays it was given, which is
 * what keeps every sortie flown without the switch bit-identical.
 */

import type { GroundQuery } from "../flight/ground.ts";
import type { FlightInputs } from "../flight/inputs.ts";
import type { RngBank } from "../flight/rng.ts";
import type { SimState } from "../flight/state.ts";
import { step } from "../flight/step.ts";
import { subsystemEnabled } from "../flight/tiers.ts";
import type { Tunables } from "../flight/tunables.ts";
import { updateAiPilot } from "./ai-pilot.ts";
import { blastShare, type BombAir, type BombImpact, type BombRecord } from "./bombs.ts";
import { bombPass, bombRunPending, rackAfter, type BombRun } from "./bomb-pass.ts";
import type { CombatAircraft } from "./encounter.ts";
import { combatEvent, type CombatEvent } from "./events.ts";
import { bodyOf } from "./geometry.ts";
import { gunPass, type Volley } from "./gun-pass.ts";
import type { RoundHit } from "./hit-allocation.ts";
import { ramPass, type RamResult } from "./ram-pass.ts";
import { advanceRoute } from "./route.ts";
import { advanceTarget, bombTarget, damageTarget, igniteTarget, type EncounterTarget } from "./targets.ts";
import { isStrategicTargetKind } from "./target-profiles.ts";
import { blindBatteryIds } from "./searchlight-posts.ts";
import { NO_SCENERY, type SceneryFire, type SceneryQuery } from "./scenery-fires.ts";
import type { CrashSite } from "./crash-sites.ts";
import { settle } from "./encounter-settle.ts";
import { buildingPass } from "./building-strike.ts";
import { sceneryPass } from "./scenery-pass.ts";
import { stepFlak, type CombatLevel, type FlakBattery, type FlakBurst, type FlakTarget } from "./flak.ts";
import { lengthVec3 } from "../flight/math3d.ts";
import { pickQuarry } from "./quarry.ts";
import type { RoundRecord } from "./rounds.ts";
import type { ShipSpec } from "./ship-deployment.ts";
import type { ShipAim } from "./ship-pass.ts";
import { steamShips, type ShipSteamResult } from "./ship-steam.ts";

export interface RosterTick {
  readonly aircraft: readonly CombatAircraft[];
  readonly targets: readonly EncounterTarget[];
  readonly flak: readonly FlakBattery[];
  readonly flakBursts: readonly FlakBurst[];
  /** Where every ship's mountings are pointing, for the renderer. Empty without ships. */
  readonly shipAims: readonly ShipAim[];
  readonly rounds: readonly RoundRecord[];
  readonly bombs: readonly BombRecord[];
  readonly bombImpacts: readonly BombImpact[];
  /** Buildings alight or freshly shot (`scenery-fires.ts`). Empty without a `SceneryQuery`. */
  readonly sceneryFires: readonly SceneryFire[];
  /** Where aeroplanes have gone in, and how long ago (`crash-sites.ts`, AD-014c). Empty until one does. */
  readonly crashSites: readonly CrashSite[];
  readonly events: readonly CombatEvent[];
  readonly tunables: Tunables;
}

export interface RosterInput {
  readonly aircraft: readonly CombatAircraft[];
  readonly targets: readonly EncounterTarget[];
  readonly flak: readonly FlakBattery[];
  /** ENHANCED SIMULATION's ships. Absent — the default — and pass 0 does nothing at all. */
  readonly ships?: readonly ShipSpec[];
  /**
   * The encounter clock at the **start** of this tick — the pre-step time, the
   * one every other pass aims against — and the only thing a ship's patrol is a
   * function of.
   *
   * `stepEncounter` passes `state.timeS`, which is exactly that. The default
   * reconstructs it as `(tick − 1) · dt`, correct for any caller stepping at a
   * fixed rate, and unreachable in practice because a caller with no ships
   * never reaches the pass that reads it.
   */
  readonly timeS?: number;
  /** Rounds still in the air from earlier ticks — they are swept for hits alongside this tick's. */
  readonly rounds: readonly RoundRecord[];
  /** Bombs still falling from earlier ticks. */
  readonly bombs: readonly BombRecord[];
  /** Buildings already shot or alight, carried between ticks (`scenery-fires.ts`). */
  readonly sceneryFires?: readonly SceneryFire[];
  /** Crash sites already made, carried between ticks (`crash-sites.ts`). */
  readonly crashSites?: readonly CrashSite[];
  readonly playerInputs: (id: string) => FlightInputs;
  readonly rng: RngBank;
  readonly tick: number;
  readonly dt: number;
  /** What is underneath everybody (`flight/ground.ts`). Flat datum when the caller has no terrain. */
  readonly ground: GroundQuery;
  /**
   * What is standing on it (`scenery-fires.ts`). `NO_SCENERY` when the caller
   * has no region, and under that default pass 3a is a no-op — which is what
   * every test, probe and pre-AD-018 caller gets.
   */
  readonly scenery?: SceneryQuery;
  /** The Realism Panel's COMBAT LEVEL, player-only and flak-only today (`flak.ts`). */
  readonly combatLevel?: CombatLevel;
}

interface Decision {
  readonly entry: CombatAircraft;
  readonly inputs: FlightInputs;
}

function decide(input: RosterInput): Decision[] {
  return input.aircraft.map((entry) => {
    const tunables = entry.sim.config.tunables;
    // The route is advanced *before* the pilot reads it, so a waypoint reached
    // on this tick is already behind the flight when it decides how to fly.
    // The player's route advances too, even though nothing flies them along it
    // — that is what the HUD's waypoint indicator counts down.
    //
    // A bomb-run leg is the one that does not end on arrival: it ends when the
    // rack does (AD-007k §10). That is a fact about racks and targets, so it is
    // decided here, where both lists are, and handed to the route module rather
    // than looked up by it.
    const route = entry.route
      ? advanceRoute(entry.route, entry.sim.position, tunables.routeCaptureRadiusM, {
          pending: bombRunPending(entry, input.targets),
          velocity: entry.sim.velocity,
        })
      : null;
    if (entry.ai === null) return { entry: { ...entry, route }, inputs: input.playerInputs(entry.id) };
    const quarry = pickQuarry(input.aircraft, input.targets, entry);
    const decision = updateAiPilot(entry.ai, {
      self: entry.sim,
      target: quarry ? quarry.body : null,
      underFire: entry.underFire,
      tunables,
      rng: input.rng,
      dt: input.dt,
      route,
      routeOrders: entry.routeOrders,
    });
    return {
      entry: { ...entry, ai: decision.pilot, route, quarryId: quarry ? quarry.id : null },
      inputs: decision.inputs,
    };
  });
}

/** One aircraft's pre-step record paired with the state its own `step` produced. */
interface SimStep {
  readonly entry: CombatAircraft;
  readonly sim: SimState;
}

function fly(decisions: readonly Decision[], dt: number, ground: GroundQuery): SimStep[] {
  return decisions.map((decision) => ({
    entry: decision.entry,
    sim: step(decision.entry.sim, decision.inputs, dt, ground),
  }));
}


function scoringEvents(hits: readonly RoundHit[]): CombatEvent[] {
  const shooters = [...new Set(hits.map((hit) => hit.shooterId))];
  return shooters.map((shooterId) => combatEvent("roundsOnTarget", shooterId, shooterId));
}

/** What ammunition each shooter had in the belt this tick, for the balloon damage model. */
function ammunitionOf(roster: readonly CombatAircraft[], shooterId: string): "regular" | "incendiary" {
  return roster.find((entry) => entry.id === shooterId)?.ammunition ?? "regular";
}

interface TargetSettlement {
  readonly targets: EncounterTarget[];
  readonly events: CombatEvent[];
}

/** Passes 3, 4, 6 and 7 for the non-flying half of the roster. */
function settleTargets(
  input: RosterInput,
  volley: Volley,
  raid: BombRun,
  tunables: Tunables,
  ram: RamResult,
): TargetSettlement {
  const events: CombatEvent[] = [];
  const targets: EncounterTarget[] = [];
  for (const before of input.targets) {
    let hit = before;
    for (const round of volley.hits.filter((entry) => entry.targetId === before.id)) {
      hit = damageTarget(hit, 1, ammunitionOf(input.aircraft, round.shooterId), round.shooterId);
    }
    for (const impact of raid.impacts) {
      hit = bombTarget(hit, blastShare(impact.position, hit, tunables), impact.bomberId);
    }
    // A gasbag flown into goes up regardless of what integrity it had left.
    const rammedBy = ram.ignitions.get(before.id);
    if (rammedBy !== undefined) hit = igniteTarget(hit, rammedBy);
    // "HE'S BURNING!" is a line about a gasbag or a shed, not about a lamp on a
    // tripod — a searchlight's half second of fire is the wreck settling, and
    // the `destroyed` event a beat later is the news. So the fire is announced
    // only for the kinds anything is decided by.
    if (!before.burning && hit.burning && isStrategicTargetKind(before.kind)) {
      events.push(combatEvent("onFire", before.id, hit.lastAttackerId));
    }
    const advanced = advanceTarget(hit, input.dt);
    if (!before.destroyed && advanced.destroyed) {
      events.push(combatEvent("destroyed", before.id, advanced.lastAttackerId));
    }
    // An armed target's belts come back off the gunnery pass, because that is
    // where its gunners spent them (`gun-pass.ts`, `airship-pass.ts`). Absent
    // for anything that did not fire, which is every kind but the Zeppelin and
    // most ticks of the Zeppelin's own sortie.
    const belt = volley.airshipBelts.get(before.id) ?? volley.shipBelts.get(before.id);
    targets.push({
      ...advanced,
      ...belt,
      ...rackAfter(advanced, raid.released.has(before.id), input.tick),
    });
  }
  return { events, targets };
}

/** What the batteries need to know about the roster, off the *pre-step* poses. */
function flakTargetsOf(before: readonly CombatAircraft[]): FlakTarget[] {
  return before.map((entry) => ({
    id: entry.id,
    side: entry.side,
    body: bodyOf(entry.sim),
    speedMps: lengthVec3(entry.sim.velocity),
    destroyed: entry.status === "destroyed",
    isPlayer: entry.ai === null,
  }));
}

/**
 * The air the whole encounter is flying in, read off the roster's first
 * aeroplane — the same place the tick takes its tunables from, and correct for
 * the same reason: `mission-spec.ts` stamps every aeroplane in an encounter with
 * one weather word and one realism block, so any of them answers for all of
 * them. It is what a *bomb* needs, since a bomb has no config of its own and a
 * Zeppelin's rack has no `sim` at all (AD-007k §9).
 */
export function encounterAir(sim: SimState): BombAir {
  return {
    enabled: subsystemEnabled("wind", sim.config.tier, sim.config.realism),
    weather: sim.config.weather,
    tunables: sim.config.tunables,
  };
}

/**
 * **Pass 0.** Puts every ship where she is at the top of this tick — steaming
 * her patrol, or going down on her last course — on both of the lists she
 * appears in: her hull among the targets and her 3-pounders among the
 * batteries, which a sunk ship leaves.
 *
 * The pass itself is `ship-steam.ts`; this is the call and the clock. Returns
 * *the arrays it was given* when there are no ships — the same reference, not a
 * copy — which is the cheap-inert idiom `building-strike.ts` established, and
 * the reason a sortie flown without ENHANCED SIMULATION cannot tell this pass
 * exists.
 */
function steam(input: RosterInput): ShipSteamResult {
  const ships = input.ships ?? [];
  if (ships.length === 0) return { targets: input.targets, flak: input.flak };
  return steamShips({
    ships,
    targets: input.targets,
    flak: input.flak,
    timeS: input.timeS ?? (input.tick - 1) * input.dt,
  });
}

/** Runs the passes and returns the new roster plus everything the tick produced. */
export function advanceRoster(input: RosterInput): RosterTick {
  const first = input.aircraft[0];
  if (!first) throw new Error("an encounter needs at least one aircraft");
  const tunables = first.sim.config.tunables;
  const air = encounterAir(first.sim);
  const steamed = steam(input);
  const roster: RosterInput = { ...input, targets: steamed.targets, flak: steamed.flak };
  const decisions = decide(roster);
  const before = decisions.map((decision) => decision.entry);
  const flown = fly(decisions, roster.dt, roster.ground);
  const archie =
    roster.flak.length > 0
      ? stepFlak({
          batteries: roster.flak,
          targets: flakTargetsOf(before),
          rng: roster.rng,
          dt: roster.dt,
          combatLevel: roster.combatLevel,
          // Read off the *pre-step* target list, like everything else this pass
          // aims with: a light shot out on this tick blinds its guns from the
          // next one, not retroactively from the shell already in the air.
          blindBatteries: blindBatteryIds(roster.targets),
        })
      : { batteries: roster.flak, bursts: [] as readonly FlakBurst[], hits: [] as readonly RoundHit[] };
  const volley = gunPass(before, flown, roster, tunables);
  // Pass 3a, over the gunnery pass's survivors: see the header on why the order
  // is the correctness rule and not an optimisation.
  const scenery = sceneryPass({
    rounds: volley.rounds,
    fires: roster.sceneryFires ?? [],
    aircraft: roster.aircraft,
    scenery: roster.scenery ?? NO_SCENERY,
    tick: roster.tick,
    dt: roster.dt,
    tunables,
  });
  const ram = ramPass(flown, roster.targets, tunables);
  // Pass 3c, on the same stepped poses and after the ram. It takes `scenery`'s
  // already-aged fire list so a barn lit on this tick gets a full `burnSeconds`.
  const strike = buildingPass(flown, roster.scenery ?? NO_SCENERY, scenery.fires);
  const raid = bombPass(before, { ...roster, tunables, air });
  const settlement = settle(strike.flown, volley, raid, roster, archie.hits, ram);
  const statics = settleTargets(roster, volley, raid, tunables, ram);
  return {
    aircraft: settlement.aircraft,
    targets: statics.targets,
    flak: archie.batteries,
    flakBursts: archie.bursts,
    shipAims: volley.shipAims,
    rounds: scenery.rounds,
    bombs: raid.bombs,
    bombImpacts: raid.impacts,
    sceneryFires: strike.fires,
    crashSites: settlement.crashSites,
    events: [...scoringEvents(volley.hits), ...settlement.events, ...statics.events],
    tunables,
  };
}
